// Consignes « format examen » (CKA / CKAD / CKS) des tâches de lab.
//
// Clé = `${labId}/${taskId}`. Reproduit la structure des énoncés réels : phrase
// d'action, spécifications en PUCES (`- `), noms/valeurs/commandes en CHIPS de
// code (`backticks`), blocs de commande (```). Fusionnées dans labs.ts (la
// consigne d'origine reste le repli). Les validateurs/hints ne changent PAS.

export const LAB_INSTRUCTIONS: Record<string, string> = {
  // ═══════════════════════════════════ CKAD (base) ═══════════════════════════
  'ckad-deploy-expose/create-deploy':
    "Dans le namespace `default`, crée un Deployment nommé `web` avec les spécifications suivantes :\n- image : `nginx:1.25`\n- nombre de replicas : `3`",
  'ckad-deploy-expose/pods-running':
    "Vérifie que les `3` pods du Deployment `web` sont bien à l'état `Running`.\n- commande : `kubectl get pods`",
  'ckad-deploy-expose/expose':
    "Expose le Deployment `web` avec un Service des spécifications suivantes :\n- type : `ClusterIP`\n- port : `80`\n- target-port : `80`",
  'ckad-deploy-expose/scale-up':
    "Le trafic augmente : porte le Deployment `web` à `5` replicas.",

  'ckad-namespaces-config/create-ns':
    "Crée un Namespace nommé `ecommerce`.",
  'ckad-namespaces-config/deploy-in-ns':
    "Dans le namespace `ecommerce`, crée un Deployment nommé `api` :\n- image : `httpd:2.4`",
  'ckad-namespaces-config/configmap':
    "Dans le namespace `ecommerce`, crée un ConfigMap nommé `api-config` :\n- donnée : `LOG_LEVEL=debug`",
  'ckad-namespaces-config/secret':
    "Dans le namespace `ecommerce`, crée un Secret générique nommé `api-db` :\n- donnée : `DB_PASSWORD=Sup3rS3cret`",

  'ckad-rollout/create':
    "Crée un Deployment nommé `payments` :\n- image : `myshop/payments:1.0`",
  'ckad-rollout/update':
    "Déploie la version 2.0 : mets à jour l'image du conteneur `payments` du Deployment `payments` :\n- nouvelle image : `myshop/payments:2.0`",
  'ckad-rollout/rollback':
    "La 2.0 est buguée. Effectue un rollback du Deployment `payments` : l'image doit repasser à `myshop/payments:1.0`.",

  'ckad-declarative/gen-apply-deploy':
    "Génère le manifeste d'un Deployment `catalog` avec `--dry-run=client -o yaml` dans le fichier `catalog.yaml`, puis applique-le :\n- image : `nginx`\n- replicas : `2`\n```\nkubectl create deployment catalog --image=nginx --replicas=2 --dry-run=client -o yaml > catalog.yaml\nkubectl apply -f catalog.yaml\n```",
  'ckad-declarative/gen-apply-pod':
    "Génère puis applique un Pod `cache` via un manifeste `cache.yaml` :\n- image : `redis:7`\n```\nkubectl run cache --image=redis:7 --dry-run=client -o yaml > cache.yaml\nkubectl apply -f cache.yaml\n```",

  'ckad-pod-run/run-pod':
    "Dans le namespace `default`, lance un Pod nommé `busybox` :\n- image : `busybox:1.36`",
  'ckad-pod-run/run-labeled':
    "Lance un Pod nommé `nginx-prod` (image `nginx`), puis ajoute-lui le label suivant :\n- `env=prod`",

  // ═══════════════════════════════════ CKA (base) ════════════════════════════
  'cka-workloads/ns': "Crée un Namespace nommé `production`.",
  'cka-workloads/deploy':
    "Dans le namespace `production`, crée un Deployment nommé `frontend` :\n- image : `nginx:1.25`",
  'cka-workloads/scale':
    "Assure la haute disponibilité : porte le Deployment `frontend` (namespace `production`) à `4` replicas.",
  'cka-workloads/expose':
    "Dans le namespace `production`, expose le Deployment `frontend` avec un Service :\n- type : `NodePort`\n- port : `80`",

  'cka-rbac-namespaced/ns': "Crée un Namespace nommé `web`.",
  'cka-rbac-namespaced/sa':
    "Dans le namespace `web`, crée un ServiceAccount nommé `monitor-sa`.",
  'cka-rbac-namespaced/role':
    "Dans le namespace `web`, crée un Role nommé `pod-reader` :\n- verbes : `get`, `list`, `watch`\n- ressources : `pods`",
  'cka-rbac-namespaced/rolebinding':
    "Dans le namespace `web`, crée un RoleBinding nommé `read-pods` :\n- Role référencé : `pod-reader`\n- sujet : ServiceAccount `web:monitor-sa`",

  'cka-rbac-cluster/sa':
    "Dans le namespace `default`, crée un ServiceAccount nommé `pipeline`.",
  'cka-rbac-cluster/clusterrole':
    "Crée un ClusterRole nommé `deploy-manager` :\n- verbes : `create`, `get`, `list`\n- ressources : `deployments`",
  'cka-rbac-cluster/crb':
    "Crée un ClusterRoleBinding nommé `pipeline-deploy` :\n- ClusterRole référencé : `deploy-manager`\n- sujet : ServiceAccount `default:pipeline`",

  // ═══════════════════════════════════ CKS (base) ════════════════════════════
  'cks-least-privilege/ns': "Crée un Namespace nommé `secure`.",
  'cks-least-privilege/secret':
    "Dans le namespace `secure`, crée un Secret générique nommé `db-creds` :\n- donnée : `PASSWORD=s3cr3t`",
  'cks-least-privilege/sa':
    "Dans le namespace `secure`, crée un ServiceAccount dédié nommé `app-sa`.",
  'cks-least-privilege/role':
    "Dans le namespace `secure`, applique le moindre privilège :\n- Role `secret-reader` : verbe `get` UNIQUEMENT, ressource `secrets`\n- RoleBinding `app-secret` : lie `secret-reader` au ServiceAccount `secure:app-sa`",

  'cks-network-policy/ns': "Crée un Namespace nommé `secure`.",
  'cks-network-policy/deny-all':
    "Applique le manifeste `deny-all.yaml` (fourni). Il crée une NetworkPolicy qui bloque TOUT le trafic entrant du namespace `secure` :\n- nom : `default-deny-ingress`\n- podSelector : `{}` (tous les pods)\n- policyTypes : `Ingress`\n- astuce : `cat deny-all.yaml` pour l'inspecter\n```\nkubectl apply -f deny-all.yaml\n```",
  'cks-network-policy/allow':
    "Applique le manifeste `allow-frontend.yaml`. Il autorise le trafic entrant vers `app=backend` depuis `app=frontend` :\n- nom : `allow-frontend`\n```\nkubectl apply -f allow-frontend.yaml\n```",

  // ═══════════════════════════════════ CKAD — série 1 ════════════════════════
  'ckad-bookstore-stack/ns': "Crée un Namespace nommé `bookstore`.",
  'ckad-bookstore-stack/cm':
    "Dans le namespace `bookstore`, crée un ConfigMap nommé `catalog-config` :\n- donnée : `APP_ENV=production`",
  'ckad-bookstore-stack/secret':
    "Dans le namespace `bookstore`, crée un Secret générique nommé `catalog-db` :\n- donnée : `DB_PASSWORD=Sup3rS3cret`",
  'ckad-bookstore-stack/deploy':
    "Dans le namespace `bookstore`, crée un Deployment nommé `catalog` :\n- image : `httpd:2.4`\n- replicas : `3`",
  'ckad-bookstore-stack/expose':
    "Dans le namespace `bookstore`, expose le Deployment `catalog` avec un Service :\n- type : `ClusterIP`\n- port : `8080`\n- target-port : `80`",
  'ckad-bookstore-stack/scale':
    "Black Friday : porte le Deployment `catalog` (namespace `bookstore`) à `5` replicas.",

  'ckad-canary/stable':
    "Applique le manifeste `stable.yaml` (fourni) — Deployment `shop-stable` :\n- image : `nginx:1.25`\n- replicas : `4`\n```\nkubectl apply -f stable.yaml\n```",
  'ckad-canary/canary':
    "Applique le manifeste `canary.yaml` — Deployment `shop-canary` (recevra ~20 % du trafic) :\n- image : `nginx:1.26`\n- replicas : `1`\n```\nkubectl apply -f canary.yaml\n```",
  'ckad-canary/svc':
    "Applique le manifeste `shop-svc.yaml` — Service `shop` :\n- sélecteur : `app=shop` (route vers les DEUX versions)\n- port : `80`\n```\nkubectl apply -f shop-svc.yaml\n```",

  'ckad-rolling-update/create':
    "Crée un Deployment nommé `web` :\n- image : `nginx:1.24`\n- replicas : `3`",
  'ckad-rolling-update/v125':
    "Mets à jour l'image du conteneur `web` du Deployment `web` :\n- nouvelle image : `nginx:1.25`",
  'ckad-rolling-update/v126':
    "Nouvelle version : mets à jour l'image du Deployment `web` :\n- nouvelle image : `nginx:1.26`",
  'ckad-rolling-update/undo':
    "La `nginx:1.26` casse la prod. Annule le dernier déploiement du Deployment `web` : l'image doit repasser à `nginx:1.25`.",

  'ckad-multi-container/apply':
    "Applique le manifeste `webapp.yaml` (fourni) — Pod `webapp` à DEUX conteneurs :\n- conteneur `app` : image `nginx:1.25`\n- sidecar `log-agent` : image `busybox:1.36`\n```\nkubectl apply -f webapp.yaml\n```",
  'ckad-multi-container/label':
    "Étiquette le Pod `webapp` avec le label suivant :\n- `tier=frontend`",

  // ═══════════════════════════════════ CKA — série 1 ═════════════════════════
  'cka-rbac-user-clusterrole/ns': "Crée un Namespace nommé `dev`.",
  'cka-rbac-user-clusterrole/clusterrole':
    "Crée un ClusterRole nommé `app-viewer` :\n- verbes : `get`, `list`, `watch`\n- ressources : `pods`, `services`, `deployments`",
  'cka-rbac-user-clusterrole/rb':
    "Dans le namespace `dev`, crée un RoleBinding nommé `dev-view` (accès limité à `dev`) :\n- ClusterRole référencé : `app-viewer`\n- sujet : User `alice`",

  'cka-rbac-group-cluster/clusterrole':
    "Crée un ClusterRole nommé `cluster-auditor` :\n- verbes : `get`, `list`\n- ressources : `pods`, `secrets`, `configmaps`, `namespaces`",
  'cka-rbac-group-cluster/crb':
    "Crée un ClusterRoleBinding nommé `audit-bind` :\n- ClusterRole référencé : `cluster-auditor`\n- sujet : Group `auditors`",

  'cka-sa-app-manager/ns': "Crée un Namespace nommé `apps`.",
  'cka-sa-app-manager/sa':
    "Dans le namespace `apps`, crée un ServiceAccount nommé `deployer`.",
  'cka-sa-app-manager/role':
    "Dans le namespace `apps`, crée un Role nommé `deploy-admin` :\n- verbes : `get`, `list`, `create`, `update`, `delete`\n- ressources : `deployments`, `pods`",
  'cka-sa-app-manager/rb':
    "Dans le namespace `apps`, crée un RoleBinding nommé `deployer-admin` :\n- Role référencé : `deploy-admin`\n- sujet : ServiceAccount `apps:deployer`",

  'cka-multi-ns-workloads/ns-staging': "Crée un Namespace nommé `staging`.",
  'cka-multi-ns-workloads/ns-prod': "Crée un Namespace nommé `prod`.",
  'cka-multi-ns-workloads/deploy-staging':
    "Dans le namespace `staging`, crée un Deployment nommé `web` :\n- image : `nginx:1.25`\n- replicas : `2`",
  'cka-multi-ns-workloads/deploy-prod':
    "Dans le namespace `prod`, crée un Deployment nommé `web` :\n- image : `nginx:1.25`\n- replicas : `4`",
  'cka-multi-ns-workloads/expose-prod':
    "Dans le namespace `prod`, expose le Deployment `web` avec un Service :\n- type : `NodePort`\n- port : `80`",

  // ═══════════════════════════════════ CKS — série 1 ═════════════════════════
  'cks-default-deny-all/ns': "Crée un Namespace nommé `restricted`.",
  'cks-default-deny-all/deny':
    "Applique le manifeste `deny-all.yaml` (fourni) — NetworkPolicy `default-deny-all` :\n- podSelector : `{}` (tous les pods)\n- policyTypes : `Ingress` ET `Egress`\n```\nkubectl apply -f deny-all.yaml\n```",

  'cks-egress-restrict/ns': "Crée un Namespace nommé `restricted`.",
  'cks-egress-restrict/egress':
    "Applique le manifeste `api-egress.yaml` (fourni) — NetworkPolicy `api-egress` :\n- podSelector : `app=api`\n- policyTypes : `Egress`\n```\nkubectl apply -f api-egress.yaml\n```",

  'cks-secret-least-priv/ns': "Crée un Namespace nommé `vault`.",
  'cks-secret-least-priv/secret':
    "Dans le namespace `vault`, crée un Secret générique nommé `app-token` :\n- donnée : `TOKEN=abc123`",
  'cks-secret-least-priv/sa':
    "Dans le namespace `vault`, crée un ServiceAccount dédié nommé `reader`.",
  'cks-secret-least-priv/role':
    "Dans le namespace `vault`, crée un Role nommé `token-reader` :\n- verbe : `get` UNIQUEMENT (aucun autre)\n- ressource : `secrets`",
  'cks-secret-least-priv/rb':
    "Dans le namespace `vault`, crée un RoleBinding nommé `reader-token` :\n- Role référencé : `token-reader`\n- sujet : ServiceAccount `vault:reader`",

  'cks-configmap-not-secret/ns': "Crée un Namespace nommé `team-a`.",
  'cks-configmap-not-secret/sa':
    "Dans le namespace `team-a`, crée un ServiceAccount nommé `ci`.",
  'cks-configmap-not-secret/role':
    "Dans le namespace `team-a`, crée un Role nommé `cm-only` :\n- verbes : `get`, `list`\n- ressource : `configmaps` (SURTOUT PAS `secrets`)",
  'cks-configmap-not-secret/rb':
    "Dans le namespace `team-a`, crée un RoleBinding nommé `ci-config` :\n- Role référencé : `cm-only`\n- sujet : ServiceAccount `team-a:ci`",

  'cks-restrict-app/ns': "Crée un Namespace nommé `shop`.",
  'cks-restrict-app/deny':
    "Applique le manifeste `deny.yaml` (fourni) — coupe tout l'ingress du namespace `shop` :\n- NetworkPolicy : `deny-ingress`\n```\nkubectl apply -f deny.yaml\n```",
  'cks-restrict-app/allow':
    "Applique le manifeste `allow-web.yaml` — autorise UNIQUEMENT `app=web` à joindre `app=api` :\n- NetworkPolicy : `allow-web-to-api`\n```\nkubectl apply -f allow-web.yaml\n```",

  // ═══════════════════════════════════ CKAD — série 2 ════════════════════════
  'ckad-microservices/ns': "Crée un Namespace nommé `shop`.",
  'ckad-microservices/backend':
    "Dans le namespace `shop`, crée un Deployment nommé `backend` :\n- image : `httpd:2.4`\n- replicas : `2`",
  'ckad-microservices/frontend':
    "Dans le namespace `shop`, crée un Deployment nommé `frontend` :\n- image : `nginx:1.25`\n- replicas : `2`",
  'ckad-microservices/svc-backend':
    "Dans le namespace `shop`, expose `backend` en interne avec un Service :\n- type : `ClusterIP`\n- port : `8080`\n- target-port : `80`",
  'ckad-microservices/svc-frontend':
    "Dans le namespace `shop`, expose `frontend` au public avec un Service :\n- type : `NodePort`\n- port : `80`",

  'ckad-config-manifests/cm':
    "Applique le manifeste `config.yaml` (fourni) — ConfigMap `app-config` à 3 clés :\n- `DATABASE_URL`, `CACHE_TTL`, `FEATURE_X`\n```\nkubectl apply -f config.yaml\n```",
  'ckad-config-manifests/secret':
    "Applique le manifeste `secret.yaml` (fourni) — Secret `app-secrets` :\n- clés : `API_KEY`, `JWT_SECRET`\n```\nkubectl apply -f secret.yaml\n```",
  'ckad-config-manifests/deploy':
    "Crée un Deployment nommé `app` :\n- image : `node:20`\n- replicas : `2`",

  'ckad-scale-to-zero/create':
    "Crée un Deployment nommé `worker` :\n- image : `busybox:1.36`\n- replicas : `4`",
  'ckad-scale-to-zero/zero':
    "Maintenance : porte le Deployment `worker` à `0` replica (sans le supprimer).",
  'ckad-scale-to-zero/restart':
    "Fin de maintenance : redémarre le Deployment `worker` à `3` replicas.",

  'ckad-blue-green/blue':
    "Applique le manifeste `blue.yaml` (fourni) — Deployment `app-blue` :\n- image : `nginx:1.25`\n```\nkubectl apply -f blue.yaml\n```",
  'ckad-blue-green/green':
    "Applique le manifeste `green.yaml` — Deployment `app-green` :\n- image : `nginx:1.26`\n```\nkubectl apply -f green.yaml\n```",
  'ckad-blue-green/switch':
    "Bascule le Service `myapp` vers la version green : applique `svc-green.yaml` :\n- sélecteur : `color=green`\n```\nkubectl apply -f svc-green.yaml\n```",

  'ckad-adapter-pod/apply':
    "Applique le manifeste `edge.yaml` (fourni) — Pod `edge` à 3 conteneurs :\n- `app` : `nginx:1.25`\n- `proxy` : `haproxy:2.9`\n- `cache` : `redis:7`\n```\nkubectl apply -f edge.yaml\n```",
  'ckad-adapter-pod/label':
    "Étiquette le Pod `edge` avec le label suivant :\n- `tier=edge`",

  'ckad-targetport/deploy':
    "Crée un Deployment nommé `api` :\n- image : `myorg/api:1.0`\n- replicas : `3`",
  'ckad-targetport/expose':
    "Expose le Deployment `api` avec un Service :\n- type : `ClusterIP`\n- port : `80`\n- target-port : `8080`",

  'ckad-rollout-history/v23':
    "Crée un Deployment nommé `site` :\n- image : `nginx:1.23`",
  'ckad-rollout-history/v24':
    "Mets à jour l'image du conteneur `nginx` du Deployment `site` :\n- nouvelle image : `nginx:1.24`",
  'ckad-rollout-history/v25':
    "Mets à jour l'image du Deployment `site` (l'historique doit contenir les 3 versions) :\n- nouvelle image : `nginx:1.25`",

  'ckad-debug-run/debug':
    "Lance un Pod de diagnostic nommé `debug` :\n- image : `busybox:1.36`",
  'ckad-debug-run/deploy':
    "Crée un Deployment nommé `web` :\n- image : `nginx:1.25`\n- replicas : `2`",
  'ckad-debug-run/expose':
    "Expose le Deployment `web` avec un Service :\n- type : `ClusterIP`\n- port : `80`",

  'ckad-ns-app-stack/ns': "Crée un Namespace nommé `v2`.",
  'ckad-ns-app-stack/cm':
    "Dans le namespace `v2`, crée un ConfigMap nommé `api-cfg` :\n- donnée : `LOG_LEVEL=info`",
  'ckad-ns-app-stack/deploy':
    "Dans le namespace `v2`, crée un Deployment nommé `api` :\n- image : `httpd:2.4`\n- replicas : `3`",
  'ckad-ns-app-stack/expose':
    "Dans le namespace `v2`, expose le Deployment `api` avec un Service :\n- type : `ClusterIP`\n- port : `80`",

  'ckad-canary-promote/v1':
    "Crée un Deployment nommé `app-v1` :\n- image : `nginx:1.25`\n- replicas : `4`",
  'ckad-canary-promote/v2':
    "Introduis la canary — crée un Deployment nommé `app-v2` :\n- image : `nginx:1.26`\n- replicas : `1`",
  'ckad-canary-promote/promote':
    "La canary est saine : porte le Deployment `app-v2` à `4` replicas.",
  'ckad-canary-promote/retire':
    "Retire l'ancienne version : porte le Deployment `app-v1` à `0` replica.",

  // ═══════════════════════════════════ CKA — série 2 ═════════════════════════
  'cka-rbac-endpoints/ns': "Crée un Namespace nommé `net`.",
  'cka-rbac-endpoints/sa':
    "Dans le namespace `net`, crée un ServiceAccount nommé `lb-controller`.",
  'cka-rbac-endpoints/role':
    "Dans le namespace `net`, crée un Role nommé `ep-watch` :\n- verbes : `get`, `list`, `watch`\n- ressources : `services`, `endpoints`",
  'cka-rbac-endpoints/rb':
    "Dans le namespace `net`, crée un RoleBinding nommé `lb-ep` :\n- Role référencé : `ep-watch`\n- sujet : ServiceAccount `net:lb-controller`",

  'cka-rbac-nodes/cr':
    "Crée un ClusterRole nommé `node-reader` :\n- verbes : `get`, `list`, `watch`\n- ressource : `nodes`",
  'cka-rbac-nodes/crb':
    "Crée un ClusterRoleBinding nommé `ops-nodes` :\n- ClusterRole référencé : `node-reader`\n- sujet : User `ops`",

  'cka-rbac-ns-admin/ns': "Crée un Namespace nommé `team-x`.",
  'cka-rbac-ns-admin/sa':
    "Dans le namespace `team-x`, crée un ServiceAccount nommé `admin-sa`.",
  'cka-rbac-ns-admin/role':
    "Dans le namespace `team-x`, crée un Role nommé `ns-admin` :\n- verbes : `get`, `list`, `create`, `update`, `delete`, `patch`\n- ressources : `pods`, `deployments`, `services`, `configmaps`",
  'cka-rbac-ns-admin/rb':
    "Dans le namespace `team-x`, crée un RoleBinding nommé `admin-bind` :\n- Role référencé : `ns-admin`\n- sujet : ServiceAccount `team-x:admin-sa`",

  'cka-ha-web/ns': "Crée un Namespace nommé `web-prod`.",
  'cka-ha-web/deploy':
    "Dans le namespace `web-prod`, crée un Deployment nommé `nginx` :\n- image : `nginx:1.25`\n- replicas : `5`",
  'cka-ha-web/expose':
    "Dans le namespace `web-prod`, expose le Deployment `nginx` avec un Service :\n- type : `NodePort`\n- port : `80`",
  'cka-ha-web/scale':
    "Pic de trafic : porte le Deployment `nginx` (namespace `web-prod`) à `8` replicas.",

  'cka-rbac-ci-group/ns': "Crée un Namespace nommé `ci`.",
  'cka-rbac-ci-group/cr':
    "Crée un ClusterRole nommé `deployer` :\n- verbes : `create`, `update`, `patch`, `get`, `list`\n- ressources : `deployments`, `replicasets`",
  'cka-rbac-ci-group/rb':
    "Dans le namespace `ci`, crée un RoleBinding nommé `ci-deploy` :\n- ClusterRole référencé : `deployer`\n- sujet : Group `ci-team`",

  'cka-two-tier/ns': "Crée un Namespace nommé `app`.",
  'cka-two-tier/db':
    "Dans le namespace `app`, crée un Deployment nommé `db` :\n- image : `postgres:16`\n- replicas : `1`",
  'cka-two-tier/api':
    "Dans le namespace `app`, crée un Deployment nommé `api` :\n- image : `myorg/api:1.0`\n- replicas : `3`",
  'cka-two-tier/svc-db':
    "Dans le namespace `app`, expose le Deployment `db` avec un Service :\n- type : `ClusterIP`\n- port : `5432`",
  'cka-two-tier/svc-api':
    "Dans le namespace `app`, expose le Deployment `api` avec un Service :\n- type : `ClusterIP`\n- port : `8080`",

  'cka-rbac-secret-writer/ns': "Crée un Namespace nommé `secrets-ns`.",
  'cka-rbac-secret-writer/sa':
    "Dans le namespace `secrets-ns`, crée un ServiceAccount nommé `rotator`.",
  'cka-rbac-secret-writer/role':
    "Dans le namespace `secrets-ns`, crée un Role nommé `secret-writer` :\n- verbes : `get`, `list`, `create`, `update`, `delete`\n- ressource : `secrets`",
  'cka-rbac-secret-writer/rb':
    "Dans le namespace `secrets-ns`, crée un RoleBinding nommé `rotator-bind` :\n- Role référencé : `secret-writer`\n- sujet : ServiceAccount `secrets-ns:rotator`",

  'cka-cluster-readonly/cr':
    "Crée un ClusterRole nommé `read-only` :\n- verbes : `get`, `list`, `watch`\n- ressources : `pods`, `services`, `deployments`, `configmaps`, `namespaces`",
  'cka-cluster-readonly/crb':
    "Crée un ClusterRoleBinding nommé `viewers-ro` :\n- ClusterRole référencé : `read-only`\n- sujet : Group `viewers`",

  'cka-scale-rollout-admin/create':
    "Crée un Deployment nommé `shop` :\n- image : `nginx:1.24`\n- replicas : `3`",
  'cka-scale-rollout-admin/update':
    "Mets à jour l'image du conteneur `shop` du Deployment `shop` :\n- nouvelle image : `nginx:1.25`",
  'cka-scale-rollout-admin/scale':
    "Porte le Deployment `shop` à `6` replicas.",

  'cka-multi-team-rbac/ns-a': "Crée un Namespace nommé `team-a`.",
  'cka-multi-team-rbac/a-access':
    "Dans le namespace `team-a`, mets en place l'accès en LECTURE :\n- ServiceAccount : `a-sa`\n- Role `a-view` : verbes `get`, `list` sur `pods`\n- RoleBinding `a-bind` : lie `a-view` au ServiceAccount `team-a:a-sa`",
  'cka-multi-team-rbac/ns-b': "Crée un Namespace nommé `team-b`.",
  'cka-multi-team-rbac/b-access':
    "Dans le namespace `team-b`, mets en place l'accès en GESTION :\n- ServiceAccount : `b-sa`\n- Role `b-manage` : verbes `get`, `list`, `create`, `delete` sur `pods`, `deployments`\n- RoleBinding `b-bind` : lie `b-manage` au ServiceAccount `team-b:b-sa`",

  // ═══════════════════════════════════ CKS — série 2 ═════════════════════════
  'cks-deny-egress-all/ns': "Crée un Namespace nommé `locked`.",
  'cks-deny-egress-all/deny':
    "Applique le manifeste `deny-egress.yaml` (fourni) — NetworkPolicy `default-deny-egress` :\n- podSelector : `{}` (tous les pods)\n- policyTypes : `Egress` uniquement\n```\nkubectl apply -f deny-egress.yaml\n```",

  'cks-internal-only/ns': "Crée un Namespace nommé `intranet`.",
  'cks-internal-only/deny':
    "Applique le manifeste `deny.yaml` (fourni) — bloque tout l'ingress du namespace `intranet` :\n- NetworkPolicy : `default-deny-ingress`\n```\nkubectl apply -f deny.yaml\n```",
  'cks-internal-only/allow':
    "Applique le manifeste `allow-internal.yaml` — autorise l'ingress uniquement depuis les pods du MÊME namespace :\n- NetworkPolicy : `allow-internal`\n```\nkubectl apply -f allow-internal.yaml\n```",

  'cks-db-isolation/ns': "Crée un Namespace nommé `data`.",
  'cks-db-isolation/deny':
    "Applique le manifeste `deny.yaml` (fourni) — coupe l'ingress du namespace `data` :\n- NetworkPolicy : `deny-ingress`\n```\nkubectl apply -f deny.yaml\n```",
  'cks-db-isolation/allow':
    "Applique le manifeste `allow-api.yaml` — n'autorise que `app=api` à joindre `app=db` :\n- NetworkPolicy : `allow-api-to-db`\n```\nkubectl apply -f allow-api.yaml\n```",

  'cks-api-egress/ns': "Crée un Namespace nommé `payments`.",
  'cks-api-egress/egress':
    "Applique le manifeste `api-egress.yaml` (fourni) — NetworkPolicy `api-egress-restrict` :\n- podSelector : `app=api`\n- policyTypes : `Egress`\n```\nkubectl apply -f api-egress.yaml\n```",

  'cks-pods-readonly/ns': "Crée un Namespace nommé `monitoring`.",
  'cks-pods-readonly/sa':
    "Dans le namespace `monitoring`, crée un ServiceAccount nommé `agent`.",
  'cks-pods-readonly/role':
    "Dans le namespace `monitoring`, crée un Role nommé `pod-viewer` :\n- verbes : `get`, `list`, `watch` UNIQUEMENT (ni `create`, ni `delete`)\n- ressource : `pods`",
  'cks-pods-readonly/rb':
    "Dans le namespace `monitoring`, crée un RoleBinding nommé `agent-view` :\n- Role référencé : `pod-viewer`\n- sujet : ServiceAccount `monitoring:agent`",

  'cks-scoped-secret/ns': "Crée un Namespace nommé `billing`.",
  'cks-scoped-secret/secret':
    "Dans le namespace `billing`, crée un Secret générique nommé `stripe-key` :\n- donnée : `KEY=sk_live_x`",
  'cks-scoped-secret/sa':
    "Dans le namespace `billing`, crée un ServiceAccount nommé `billing-app`.",
  'cks-scoped-secret/role':
    "Dans le namespace `billing`, applique le moindre privilège :\n- Role `key-reader` : verbe `get` sur `secrets`\n- RoleBinding `app-key` : lie `key-reader` au ServiceAccount `billing:billing-app`",

  'cks-audit-events/cr':
    "Crée un ClusterRole nommé `security-audit` :\n- verbes : `get`, `list`, `watch`\n- ressources : `pods`, `events`, `serviceaccounts`, `networkpolicies`",
  'cks-audit-events/crb':
    "Crée un ClusterRoleBinding nommé `sec-audit` :\n- ClusterRole référencé : `security-audit`\n- sujet : Group `security`",

  'cks-three-tier-netpol/ns': "Crée un Namespace nommé `chain`.",
  'cks-three-tier-netpol/web':
    "Applique le manifeste `np-web.yaml` (fourni) — politique ingress du tier web :\n- NetworkPolicy `web-ingress` : podSelector `tier=web`\n```\nkubectl apply -f np-web.yaml\n```",
  'cks-three-tier-netpol/api':
    "Applique le manifeste `np-api.yaml` — le tier api n'accepte QUE le tier web :\n- NetworkPolicy `api-ingress` : podSelector `tier=api`\n```\nkubectl apply -f np-api.yaml\n```",
  'cks-three-tier-netpol/db':
    "Applique le manifeste `np-db.yaml` — le tier db n'accepte QUE le tier api :\n- NetworkPolicy `db-ingress` : podSelector `tier=db`\n```\nkubectl apply -f np-db.yaml\n```",

  'cks-configmap-scoped/ns': "Crée un Namespace nommé `ext-tool`.",
  'cks-configmap-scoped/sa':
    "Dans le namespace `ext-tool`, crée un ServiceAccount nommé `importer`.",
  'cks-configmap-scoped/role':
    "Dans le namespace `ext-tool`, crée un Role nommé `cfg-reader` :\n- verbes : `get`, `list`\n- ressource : `configmaps` UNIQUEMENT (jamais `secrets`)",
  'cks-configmap-scoped/rb':
    "Dans le namespace `ext-tool`, crée un RoleBinding nommé `importer-cfg` :\n- Role référencé : `cfg-reader`\n- sujet : ServiceAccount `ext-tool:importer`",

  'cks-frontend-egress/ns': "Crée un Namespace nommé `edge`.",
  'cks-frontend-egress/ingress':
    "Applique le manifeste `fe-deny-ingress.yaml` (fourni) — aucun ingress direct vers `app=frontend` :\n- NetworkPolicy `fe-deny-ingress` : podSelector `app=frontend`, policyTypes `Ingress`\n```\nkubectl apply -f fe-deny-ingress.yaml\n```",
  'cks-frontend-egress/egress':
    "Applique le manifeste `fe-egress.yaml` — contrôle de l'egress de `app=frontend` :\n- NetworkPolicy `fe-egress` : podSelector `app=frontend`, policyTypes `Egress`\n```\nkubectl apply -f fe-egress.yaml\n```",
};
